import { initializeApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider } from 'firebase/auth'
import {
  getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
} from 'firebase/firestore'

const required = (key: string): string => {
  const value = import.meta.env[key as keyof ImportMetaEnv] as string | undefined
  if (!value) {
    throw new Error(
      `Missing ${key}. Copy .env.local.example to .env.local and fill it from the ` +
        `Firebase console (Project settings → Your apps → Web app config).`,
    )
  }
  return value
}

export const app = initializeApp({
  apiKey: required('VITE_FIREBASE_API_KEY'),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: required('VITE_FIREBASE_PROJECT_ID'),
  messagingSenderId: required('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: required('VITE_FIREBASE_APP_ID'),
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as string | undefined,
})

/** The project the app is talking to — the Admin usage card links into its console. */
export const PROJECT_ID = required('VITE_FIREBASE_PROJECT_ID')

export const auth = getAuth(app)
/**
 * Firestore, with the documents it has already seen kept on the device.
 *
 * Every tab used to re-read every collection it opened, from the server, on every reload:
 * 928 documents for the field guide, 364 for the product list, and so on for each tab
 * somebody visits. None of that had changed since the last time they looked.
 *
 * With a persistent cache a listener resumes from what is already on the device and asks
 * the server only for what has changed since — so a reload costs close to nothing, and the
 * screen fills instantly instead of after a round trip. Nothing about the live behaviour
 * changes: the listeners still deliver other people's edits as they happen.
 *
 * The multi-tab manager is what makes that safe with the tracker open in several tabs,
 * which is how it is actually used — one on the roadmap, one on the products.
 *
 * IndexedDB can be unavailable (a private window, a browser configured to refuse site
 * data). The fall-back is the ordinary in-memory cache, which is exactly what this was
 * before, so the app works either way.
 */
export const db = (() => {
  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    })
  } catch {
    return getFirestore(app)
  }
})()

export const googleProvider = new GoogleAuthProvider()
// Hints Google's account chooser toward a work account. The real restriction is in the
// security rules — a hint is a convenience, never a control.
googleProvider.setCustomParameters({ prompt: 'select_account' })

// VITE_FIREBASE_AUTH_DOMAIN must stay as the *.firebaseapp.com domain.
//
// Firebase provisions exactly one authorised OAuth redirect URI on the Google client:
// https://<project>.firebaseapp.com/__/auth/handler. Setting authDomain to the hosting
// origin (…web.app) makes the SDK send …web.app/__/auth/handler as the redirect_uri,
// which is not registered, and Google rejects sign-in with
// "Error 400: redirect_uri_mismatch".
//
// Pointing it at the hosting origin is the documented fix for *redirect* sign-in, where a
// cross-origin auth domain means the state cookie is third-party and gets dropped by
// tracking protection. It requires also adding that handler URL to the OAuth client in the
// Google Cloud console. We use popup sign-in, which has no such cookie problem, so the
// default is correct here and changing it only breaks things.
